/**
 * Choosing an item, out of a world that has a great many of them.
 *
 * A plain dropdown of every item is fine while there are twenty and useless
 * once there are two hundred: a fishing spot asking which tool it needs should
 * offer nets and rods, not doors and coins. So this one is told which tag fits
 * the job and shows those first, with everything else a click away for the
 * cases nobody thought of when the tags were written.
 */

import { useMemo, useState } from 'react';
import type { ItemDefinitionWire } from '@inverse/protocol';

export function ItemPicker({
  items,
  value,
  tag,
  allowNothing,
  nothingLabel = 'nothing',
  onPick,
}: {
  items: readonly ItemDefinitionWire[];
  /** The item chosen now, or zero for nothing. */
  value: number;
  /**
   * The tag that suits this job, if there is one.
   *
   * Items carrying it are what the list offers. Nothing carrying it, and the
   * list quietly offers everything rather than being empty and useless.
   */
  tag?: string;
  /** Whether zero is a real answer here, as it is for a tool nothing needs. */
  allowNothing?: boolean;
  nothingLabel?: string;
  onPick: (itemId: number) => void;
}): JSX.Element {
  const [everything, setEverything] = useState(false);

  const fitting = useMemo(
    () => (tag ? items.filter((item) => item.tags.includes(tag)) : []),
    [items, tag],
  );

  // The chosen one is always in the list, even when it does not fit the tag:
  // a list that cannot show what is already set is a list that loses it.
  const shown = useMemo(() => {
    const held = !tag || everything || fitting.length === 0 ? [...items] : [...fitting];
    if (value > 0 && !held.some((item) => item.id === value)) {
      const chosen = items.find((item) => item.id === value);
      if (chosen) held.unshift(chosen);
    }

    return held;
  }, [items, fitting, tag, everything, value]);

  const filtering = tag !== undefined && fitting.length > 0;

  return (
    <span className="item-picker">
      <select value={value} onChange={(event) => onPick(Number(event.target.value))}>
        {allowNothing ? <option value={0}>{nothingLabel}</option> : null}
        {shown.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
        {value > 0 && !shown.some((item) => item.id === value) ? (
          <option value={value}>Item {value}</option>
        ) : null}
      </select>

      {filtering ? (
        <button
          type="button"
          className={everything ? 'editor-tab on' : 'editor-tab'}
          title={
            everything
              ? `Show only what is tagged ${tag}`
              : `Showing what is tagged ${tag}. Press for everything.`
          }
          onClick={() => setEverything(!everything)}
        >
          {everything ? 'All' : tag.replace(/^[a-z]+_/, '')}
        </button>
      ) : null}
    </span>
  );
}
